<template>
  <div class="v-accordion-with-media-mts">
    <div class="v-accordion-with-media-mts__accordion">
      <div
        v-for="(item, index) in items"
        :key="index"
        :class="[
          'v-accordion-with-media-mts__accordion-item',
          { '--active': index === currentTabIndex },
        ]"
      >
        <div
          class="v-accordion-with-media-mts__accordion-item_heading"
          @click="toggleItem(index)"
        >
          <slot name="btn" :item-btn="item">
            <div class="v-accordion-with-media-mts__accordion-item_heading-title">
              <span>{{ item.title }}</span>
              <a v-if="item.link && index === currentTabIndex" :href="item.link" target="_blank">
                <VIconMTS
                  v-if="item.link"
                  name="new-design/icon-link"
                  width="24"
                  height="24"
                  fill="#1D2023"
                />
              </a>
            </div>
            <VIconMTS
              v-if="withIcon"
              :name="withIcon.name"
              :width="withIcon.width"
              :height="withIcon.height"
              :fill="withIcon.fill"
              class="v-accordion-with-media-mts__accordion-item_heading-icon"
            />
          </slot>
        </div>

        <transition name="slide-jquery" @enter="enter" @leave="leave">
          <div
            v-if="index === currentTabIndex"
            class="v-accordion-with-media-mts__accordion-item_content"
            :key="index"
          >
            <div
              v-if="isTablet && withMedia"
              class="v-accordion-with-media-mts__accordion-item_media-wrapper"
            >
              <video
                v-if="!withImage && items[currentTabIndex]['video']"
                ref="videoTabletElement"
                :key="currentTabIndex"
                autoplay="true"
                preload="auto"
                loop="loop"
                muted="true"
                playsinline="playsinline"
                @loadedmetadata="handleVideoMetadata"
              >
                <source
                  :src="require(`~/assets/${baseVideoPath}/${item.video}.mp4`)"
                  type="video/mp4"
                />
                <source
                  :src="require(`~/assets/${baseVideoPath}/${item.video}.webm`)"
                  type="video/webm"
                />
              </video>

              <picture v-else-if="items[currentTabIndex]['image']">
                <source
                    :srcset="require(`@/assets/${baseImagePath}/${items[currentTabIndex]['image']}.webp`)"
                    type="image/webp"
                />
                <img :src="require(`@/assets/${baseImagePath}/${items[currentTabIndex]['image']}.jpg`)" alt="" />
              </picture>

              <VButtonMTS
                v-if="autoPlay"
                @click="toggleAutoPlay"
                class="v-accordion-with-media-mts__media-btn"
              >
                <VIconMTS
                  :name="`mts/icon-mts--${isPlaying ? 'pause' : 'play'}`"
                  width="16"
                  height="16"
                  fill="#ffffff"
                />
              </VButtonMTS>
            </div>

            <slot name="content" :item-content="item.content">
              {{ item.content }}
            </slot>
          </div>
        </transition>

        <div class="v-accordion-with-media-mts__accordion-item_progress-bar">
          <span
            v-if="index === currentTabIndex && withProgressBar"
            :style="{ width: progressPercentage + '%' }"
          ></span>
        </div>
      </div>
      <slot name="button"></slot>
    </div>
    <div
      v-if="!isTablet && withMedia"
      class="v-accordion-with-media-mts__media-wrapper"
    >
      <video
        v-if="!withImage && items[currentTabIndex]['video']"
        ref="videoElement"
        :key="currentTabIndex"
        autoplay="true"
        preload="auto"
        loop="loop"
        muted="true"
        playsinline="playsinline"
        @loadedmetadata="handleVideoMetadata"
      >
        <source
          :src="
            require(`~/assets/${baseVideoPath}/${items[currentTabIndex]['video']}.mp4`)
          "
          type="video/mp4"
        />
        <source
          :src="
            require(`~/assets/${baseVideoPath}/${items[currentTabIndex]['video']}.webm`)
          "
          type="video/webm"
        />
      </video>

      <picture v-else-if="items[currentTabIndex]['image']">
        <source
          :srcset="require(`@/assets/${baseImagePath}/${items[currentTabIndex]['image']}.webp`)"
          type="image/webp"
        />
        <img :src="require(`@/assets/${baseImagePath}/${items[currentTabIndex]['image']}.jpg`)" alt="" />
      </picture>

      <VButtonMTS
        v-if="autoPlay"
        @click="toggleAutoPlay"
        class="v-accordion-with-media-mts__media-btn"
      >
        <VIconMTS
          :name="`mts/icon-mts--${isPlaying ? 'pause' : 'play'}`"
          width="16"
          height="16"
          fill="#ffffff"
        />
      </VButtonMTS>
    </div>
  </div>
</template>

<script>
import VIconMTS from '~/components/Common/MTS/VIconMTS.vue'
import VButtonMTS from '~/components/Common/MTS/VButtonMTS.vue'

export default {
  name: 'VAccordionWithProgressBarMTS',
  components: {
    VIconMTS,
    VButtonMTS,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    duration: {
      type: Number,
      default: 5000, // Длительность отображения одной вкладки
    },
    withIcon: {
      type: Object,
      default: () => {},
    },
    withMedia: {
      type: Boolean,
      default: false,
    },
    withProgressBar: {
      type: Boolean,
      default: false,
    },
    autoPlay: {
      type: Boolean,
      default: false,
    },
    withImage: {
      type: Boolean,
      default: false,
    },
    baseImagePath: {
      type: String,
      default: 'img',
    },
    baseVideoPath: {
      type: String,
      default: 'video',
    }
  },
  data() {
    return {
      windowWidth: null,
      progressPercentage: 0, // Текущий процент выполнения прогресса
      currentTabIndex: 0, // Индекс текущей активной вкладки
      isPlaying: true, // Указывает, работает ли анимация (воспроизведение/пауза)
      lastStartTime: null, // Время последнего запуска/возобновления таймера
      elapsedTime: 0, // Сколько времени прошло с момента старта/переключения
      animationFrameId: null, // ID текущего анимационного кадра
      currentDuration: 5000, // Актуальная длительность текущей вкладки в мс
    }
  },
  computed: {
    isTablet() {
      return this.windowWidth <= 991
    },
  },
  watch: {
    // При смене активной вкладки перезапускаем прогресс
    currentTabIndex() {
      if (this.autoPlay) {
        this.resetProgress()
        this.startAutoPlay()
      }
    },
    // windowWidth() {
    //   if (this.autoPlay) {
    //     this.resetProgress()
    //     this.startAutoPlay()
    //   }
    // },
  },
  mounted() {
    window.addEventListener('resize', this.handleResize)
    window.addEventListener('orientationchange', this.handleResize)
    this.handleResize()
    // Если включено автопроигрывание, запускаем автопроигрывание
    if (this.autoPlay) {
      this.startAutoPlay()
    }
  },
  beforeDestroy() {
    if (this.autoPlay) {
      this.stopAutoPlay() // Очищаем интервалы перед уничтожением компонента
    }
    window.removeEventListener('resize', this.handleResize)
    window.removeEventListener('orientationchange', this.handleResize)
  },
  methods: {
    // handleResize() {
    //   this.windowWidth = window.innerWidth
    // },
    //Метод получения актуально элемента с видео
    getCurrentVideoElement() {
      if (this.isTablet) {
        return this.$refs.videoTabletElement?.[this.currentTabIndex] || null
      } else {
        return this.$refs.videoElement || null
      }
    },

    // Метод для обновления длительности вкладки в зависимости от видео
    updateDuration() {
      const video = this.getCurrentVideoElement()
      if (!video) return
      if (this.withMedia && video?.readyState >= 1) {
        // Если видео есть и готово, используем его длительность
        this.currentDuration = video.duration * 1000 // Длительность видео в мс
      } else {
        // Если видео нет, возвращаем значение по умолчанию
        this.currentDuration = this.duration
      }
    },

    // Метод для запуска автопроигрывания
    startAutoPlay() {
      if (!this.autoPlay) return // Проверяем наличие настроек
      this.isPlaying = true
      this.lastStartTime = performance.now() - this.elapsedTime // Учитываем уже прошедшее время
      this.updateDuration() // Устанавливаем текущую длительность
      this.animateProgress() // Запускаем прогресс-бар

      // Если с медиа и видео существует, включаем его воспроизведение
      if (this.withMedia) {
        const video = this.getCurrentVideoElement()
        if (!video) return
        if (video && video.paused) {
          video.play().catch((e) => console.warn('Ошибка запуска видео:', e))
        }
      }
    },

    // Метод для остановки автопроигрывания
    stopAutoPlay() {
      if (!this.autoPlay) return // Проверяем наличие настроек
      this.isPlaying = false
      cancelAnimationFrame(this.animationFrameId) // Останавливаем анимацию
      this.elapsedTime = performance.now() - this.lastStartTime // Запоминаем, сколько времени прошло

      // Если с медиа и видео существует, ставим его на паузу
      if (this.withMedia) {
        const video = this.getCurrentVideoElement()
        if (!video) return
        if (video && !video.paused) {
          video.pause()
        }
      }
    },

    // Метод для переключения между Play/Pause
    toggleAutoPlay() {
      if (!this.autoPlay) return // Проверяем наличие настроек
      if (this.isPlaying) {
        this.stopAutoPlay() // Ставим автоанимацию и видео на паузу
      } else {
        this.startAutoPlay() // Запускаем автоанимацию и воспроизведение видео
      }
    },

    // Метод для перехода на следующую вкладку
    nextTab() {
      this.currentTabIndex = (this.currentTabIndex + 1) % this.items.length // Переход на следующий индекс
      if (this.autoPlay) this.resetProgress()
    },
    // Метод для сброса прогресса при переключении вкладки
    resetProgress() {
      if (!this.autoPlay) return // Проверяем, нужен ли прогресс-бар
      if (this.withProgressBar) this.progressPercentage = 0 // Обнуляем прогресс
      this.elapsedTime = 0 // Обнуляем прошло времени
      this.lastStartTime = performance.now() // Перезаписываем время старта
    },

    // Метод анимации прогресс-бара
    animateProgress() {
      if (!this.isPlaying) return // Если воспроизведение на паузе или прогресс-бар выключен, не выполняем анимацию

      const now = performance.now()
      const timePassed = now - this.lastStartTime // Время, прошедшее с момента старта/возобновления
      this.progressPercentage = Math.min(
        (timePassed / this.currentDuration) * 100,
        100
      ) // Рассчитываем прогресс в %

      // Если прогресс завершился, переключаем вкладку
      if (this.progressPercentage >= 100) {
        this.nextTab()
        this.lastStartTime = performance.now() // Обновляем время старта
      } else {
        this.animationFrameId = requestAnimationFrame(this.animateProgress) // Перезапускаем анимацию
      }
    },

    // Обработчик клика по вкладке
    toggleItem(index) {
      this.currentTabIndex =
        this.currentTabIndex === index ? this.currentTabIndex : index // Переключаем вкладку
      // // Если включён автоплей и прогресс-бар, останавливаем их при ручном переключении
      // if (this.autoPlay) {
      //   this.stopAutoPlay() // Останавливаем воспроизведение
      this.resetProgress() // Сбрасываем прогресс
      // }
    },
    handleVideoMetadata() {
      this.updateDuration() // Обновляем длительность, если метаданные загрузились
    },

    enter(el, done) {
      el.style.maxHeight = '0'
      requestAnimationFrame(() => {
        el.style.transition = 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)'
        el.style.maxHeight = `${el.scrollHeight}px`
      })

      const onTransitionEnd = () => {
        // После завершения анимации сбрасываем max-height для адаптивности
        el.style.maxHeight = 'none'
        // Удаляем transition чтобы избежать конфликтов при ресайзе
        el.style.transition = 'none'
        done()
      }

      el.addEventListener('transitionend', onTransitionEnd, { once: true })
    },

    leave(el, done) {
      // Перед началом анимации закрытия получаем актуальную высоту
      el.style.maxHeight = `${el.scrollHeight}px`

      requestAnimationFrame(() => {
        // Восстанавливаем transition для анимации
        el.style.transition = 'all 400ms cubic-bezier(0.4, 0, 0.2, 1)'
        el.style.maxHeight = '0'
      })

      el.addEventListener('transitionend', done, { once: true })
    },

    handleResize() {
      this.windowWidth = window.innerWidth
      // Обновляем высоту открытого элемента после изменения размера
      this.$nextTick(() => {
        const openItem = this.$el.querySelector(
          '.--active .v-accordion-with-media-mts__accordion-item_content'
        )
        if (openItem) {
          openItem.style.maxHeight = 'none' // Сбрасываем ограничения
        }
      })
    },
  },
}
</script>
<style lang="scss" scoped>
.v-accordion-with-media-mts {
  margin: 32px 0;
  display: flex;
  justify-content: space-between;
  column-gap: 32px;
  @include screen-up('lg') {
    min-height: 488px;
  }
  @include screen-up('xl') {
    min-height: 512px;
  }
  @include screen-up(1440px) {
    min-height: 556px;
  }

  &__accordion {
    display: flex;
    flex-direction: column;
    flex: 1 412px;
    // max-width: 412px;
    &-item {
      $item: &;
      position: relative;
      padding: 16px 0;
      @include hover-focus-active {
        &:not(.--active) #{$item}_heading {
          color: $color-gray-1;

          &-icon {
            opacity: 1;
          }
        }
      }

      &_heading {
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 500;
        font-size: 24px;
        line-height: 28px;
        color: #969fa8;
        transition: all 0.275s ease;
        cursor: pointer;

        .--active & {
          color: $color-dark-2;
          cursor: default;
        }

        &-title {
          display: flex;
          align-items: center;
          gap: 8px;

          a {
            line-height: 0;

            @include screen-down('lg') {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 44px;
              height: 28px;
            }
          }

          svg {
            flex-shrink: 0;
          }
        }

        &-icon {
          flex-shrink: 0;
          opacity: 0;
          visibility: visible;
          transition: all 0.275s ease;
          @include screen-down('lg') {
            opacity: 1;
          }

          .--active & {
            opacity: 0;
            visibility: hidden;
            transition: all 0.275s ease;
          }
        }
      }

      &_content {
        font-weight: 400;
        font-size: 17px;
        line-height: 24px;
        text-align: left;
        color: $color-gray-1;
        height: auto;
        overflow: hidden;
        @include screen-up('lg') {
          margin-top: 6px;
        }
        @include screen-down('lg') {
          margin-top: 16px;
        }
      }

      &_media {
        &-wrapper {
          position: relative;
          max-width: 932px;
          height: 100%;
          border-radius: 32px;
          overflow: hidden;
          @include screen-down('lg') {
            margin-bottom: 16px;
          }
        }

        &-btn {
          position: absolute;
          width: fit-content;
          right: 24px;
          bottom: 24px;
          top: auto;
          left: auto;
          background: #fcf7f126;
          backdrop-filter: blur(16px);
          padding: 16px 32px;
          border-radius: 18px;
          transition: all 0.275s ease;

          &:hover {
            background: #fcf7f173;
          }
        }
      }

      &_progress-bar {
        display: block;
        position: absolute;
        width: 100%;
        height: 1px;
        top: auto;
        bottom: 0;
        left: 0;
        background: #e2e5eb;
        overflow: hidden;

        span {
          display: block;
          width: 100%;
          height: 100%;
          background: #1d2023;
          transition: transform linear;
        }
      }
    }
  }

  &__media {
    &-wrapper {
      position: relative;
      flex: 0 932px;
      max-width: 932px;
      height: 100%;
      border-radius: 32px;
      overflow: hidden;
      @include screen-up('lg') {
        max-height: 606px;
      }
    }

    &-btn {
      position: absolute;
      max-width: 80px;
      right: 24px;
      bottom: 24px;
      top: auto;
      left: auto;
      background: #fcf7f126;
      backdrop-filter: blur(16px);
      padding: 16px 32px;
      border-radius: 18px;

      &:hover {
        background: #fcf7f173;
      }
    }
  }

  video {
    display: block;
    width: 100%;
    height: auto;
  }
}

.slide-jquery-enter-active,
.slide-jquery-leave-active {
  overflow: hidden;
  transition: all 400ms cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-jquery-enter-from,
.slide-jquery-leave-to {
  max-height: 0;
  // opacity: 0;
}

.slide-jquery-enter-to,
.slide-jquery-leave-from {
  max-height: 1100px; /* Запасное значение */
  // opacity: 1;
}
</style>
